import { PropsWithChildren, ReactNode, forwardRef } from 'react';
import { Helmet } from 'react-helmet';

import { makeStyles } from '@material-ui/core';

import classNames from 'classnames';

import { configApiRef, useApi } from '@backstage/core-plugin-api';

import { ScrollTopButton } from '../ScrollTopButton';
import { BackstagePage } from './BackstagePage';
import { Content } from './Content';
import { Header, HeaderProps } from './Header';
import { HeaderContent } from './HeaderContent';
import { NarrowBoxProps } from './NarrowBox';
import { SidebarContent, SidebarContentProps } from './SidebarContent';

const useStyles = makeStyles(theme => ({
  content: { paddingBottom: theme.spacing(12) },
  // min-height keeps the pageContent fr track from growing past the page —
  // fr tracks have an auto minimum, which would force the page to scroll.
  fullViewportContent: {
    minHeight: 0,
    overflow: 'hidden',
    paddingBottom: theme.spacing(4),
  },
}));

export type PageLayoutProps = PropsWithChildren &
  Pick<
    HeaderProps,
    | 'title'
    | 'headerBreadcrumbs'
    | 'headerTabs'
    | 'stickyHeader'
    | 'stickyHeaderTabs'
    | 'headerTabsRef'
    | 'isLoading'
    | 'pageTitleOverride'
    | 'enableMargin'
  > &
  Pick<SidebarContentProps, 'sideBarRight' | 'sideBarLeft'> & {
    narrow?: boolean | NarrowBoxProps['width'];
    headerContent?: ReactNode;
    scrollButton?: boolean;
    noPadding?: boolean;
    floatingHeader?: ReactNode;
    subheader?: ReactNode;
    fullViewport?: boolean;
  };

export const PageLayout = forwardRef<HTMLDivElement, PageLayoutProps>(
  (
    {
      children,
      floatingHeader,
      sideBarRight,
      sideBarLeft,
      title,
      enableMargin,
      headerBreadcrumbs,
      headerTabs,
      headerContent,
      stickyHeader,
      stickyHeaderTabs,
      headerTabsRef,
      isLoading,
      pageTitleOverride,
      scrollButton = false,
      narrow,
      noPadding,
      subheader,
      fullViewport,
    },
    ref,
  ) => {
    const classes = useStyles();

    const hasHeader =
      title ||
      headerBreadcrumbs ||
      headerTabs ||
      headerContent ||
      subheader ||
      isLoading;

    const configApi = useApi(configApiRef);
    const appTitle = configApi.getOptionalString('app.title') || 'Backstage';

    return (
      <BackstagePage ref={ref} fullViewport={fullViewport}>
        {floatingHeader}
        {scrollButton && <ScrollTopButton />}
        {hasHeader ? (
          <Header
            enableMargin={enableMargin}
            title={title}
            subheader={subheader}
            isLoading={isLoading}
            pageTitleOverride={pageTitleOverride}
            narrow={narrow}
            headerBreadcrumbs={headerBreadcrumbs}
            headerTabs={headerTabs}
            stickyHeader={stickyHeader}
            stickyHeaderTabs={stickyHeaderTabs}
            headerTabsRef={headerTabsRef}
          >
            {headerContent ? (
              <HeaderContent>{headerContent}</HeaderContent>
            ) : null}
          </Header>
        ) : (
          pageTitleOverride && (
            <Helmet defaultTitle={`${pageTitleOverride} | ${appTitle}`} />
          )
        )}

        <Content
          narrow={narrow}
          className={classNames(noPadding ? undefined : classes.content, {
            [classes.fullViewportContent]: fullViewport,
          })}
          noPadding={noPadding}
        >
          {sideBarLeft || sideBarRight ? (
            <SidebarContent
              sideBarLeft={sideBarLeft}
              sideBarRight={sideBarRight}
            >
              {children}
            </SidebarContent>
          ) : (
            children
          )}
        </Content>
      </BackstagePage>
    );
  },
);
PageLayout.displayName = 'PageLayout';
